import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Layout from "../components/Layout";
import { API_BASE } from "../api/config";
import "./AllShopSalesReport.css";

// ALL_SHOP_SALE_SOURCE_COLUMN_V1

const SHOPS = [
  "ALL",
  "DNGHT",
  "DNGPF",
  "GMP",
  "JCHT",
  "JCPF",
  "JS",
  "MDYHT",
  "MDYPF",
  "MT",
  "OSHT",
  "OSPF",
  "STHT",
  "STPF",
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

function money(value) {
  return Number(value || 0).toLocaleString();
}

function formatDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function excelDateText(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function isFirstVoucherRow(row, index, reportRows) {
  if (index === 0) return true;
  const previous = reportRows[index - 1];
  return row?.voucher_no !== previous?.voucher_no || row?.shop_code !== previous?.shop_code;
}

function getPromotionItems(row) {
  const sale = row?.raw_sale || {};
  const savedGifts = sale.gift_items_json;
  const rawGifts = sale.raw_sale_json?.giftItems;
  const gifts = Array.isArray(savedGifts) && savedGifts.length
    ? savedGifts
    : Array.isArray(rawGifts) && rawGifts.length
      ? rawGifts
      : sale.raw_sale_json?.promotionGiftItem
        ? [sale.raw_sale_json.promotionGiftItem]
        : [];

  return gifts
    .filter((gift) => {
      const status = String(gift?.giftStatus || "").toUpperCase();
      return gift?.giftDeclinedByCustomer !== true &&
        status !== "DECLINED_BY_CUSTOMER" &&
        status !== "DECLINED" &&
        (!status || status === "CLAIMED" || gift?.giftClaimed === true);
    })
    .map((gift) => {
      const name = String(gift?.name || gift?.giftItemName || gift?.code || gift?.giftItemCode || "").trim();
      if (!name) return "";
      const qty = Number(gift?.qty ?? gift?.giftQty ?? 1);
      return `${name} x${Number.isFinite(qty) && qty > 0 ? qty : 1}`;
    })
    .filter(Boolean)
    .join(" + ");
}

export default function AllShopSalesReport() {
  const [shop, setShop] = useState("ALL");
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // ALL_SHOP_REPORT_VOUCHER_PRICE_EDIT_V1
  const [editVoucher, setEditVoucher] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  // ALL_SHOP_ITEM_NOTE_COLUMN_V2
  const getItemNote = (row) =>
    String(
      row?.raw_item?.note ||
      row?.raw_item?.raw_item_json?.note ||
      row?.item_note ||
      row?.note ||
      ""
    ).trim();

  const query = useMemo(() => {
    const q = new URLSearchParams();
    q.set("shop", shop);
    q.set("startDate", startDate);
    q.set("endDate", endDate);
    q.set("search", search);
    q.set("limit", "20000");
    return q.toString();
  }, [shop, startDate, endDate, search]);

  async function loadReport() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(`${API_BASE}/api/erp/sales-report/all-shops?${query}`);
      const json = await res.json().catch(() => ({}));

      if (!res.ok || json.success === false) {
        throw new Error(json.error || json.message || "Sales report loading failed");
      }

      setRows(Array.isArray(json.rows) ? json.rows : []);
      setTotal(Number(json.total || 0));
    } catch (err) {
      setError(err.message || String(err));
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const summary = useMemo(() => {
    const voucherSet = new Set();
    let qty = 0;
    let amount = 0;
    let grandTotalByVoucher = new Map();

    for (const row of rows) {
      const voucher = String(row.voucher_no || "");
      if (voucher) voucherSet.add(voucher);
      qty += Number(row.qty || 0);
      amount += Number(row.amount || 0);

      if (voucher && !grandTotalByVoucher.has(voucher)) {
        grandTotalByVoucher.set(voucher, Number(row.grand_total || 0));
      }
    }

    const grandTotal = Array.from(grandTotalByVoucher.values()).reduce((a, b) => a + b, 0);

    return {
      vouchers: voucherSet.size,
      qty,
      amount,
      grandTotal,
    };
  }, [rows]);

  function openVoucherPriceEditor(row) {
    const voucherNo = String(row?.voucher_no || "");

    const voucherRows = rows.filter(
      (item) => String(item?.voucher_no || "") === voucherNo
    );

    const items = voucherRows.map((item, index) => ({
      id:
        item?.raw_item?.id ||
        item?.item_id ||
        item?.rawItem?.id ||
        "",
      lineNo:
        item?.raw_item?.line_no ||
        item?.line_no ||
        index + 1,
      productCode: item?.product_code || "",
      color: item?.color || "",
      size: item?.size || "",
      qty: Number(item?.qty || 0),
      oldPrice: Number(item?.price || 0),
      unitPrice: Number(item?.price || 0),
    }));

    const missingId = items.find((item) => !item.id);

    if (missingId) {
      alert(
        "Sale item ID á€™á€›á€•á€«á‹ Backend report response á€€á€­á€¯ Refresh á€œá€¯á€•á€ºá€•á€¼á€®á€¸ á€•á€¼á€”á€ºá€…á€™á€ºá€¸á€•á€«á‹"
      );
      return;
    }

    setEditVoucher({
      voucherNo,
      shopCode: row?.shop_code || "",
      paymentMethod: row?.payment_method || "",
    });

    setEditItems(items);
  }

  function updateEditItemPrice(index, value) {
    setEditItems((previous) =>
      previous.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              unitPrice: value,
            }
          : item
      )
    );
  }

  const editedVoucherTotal = editItems.reduce(
    (sum, item) =>
      sum +
      Number(item?.qty || 0) *
        Number(item?.unitPrice || 0),
    0
  );

  async function saveVoucherPriceCorrection() {
    if (!editVoucher?.voucherNo) return;

    const invalidItem = editItems.find(
      (item) =>
        !Number.isFinite(Number(item.unitPrice)) ||
        Number(item.unitPrice) < 0
    );

    if (invalidItem) {
      alert("Price á€á€”á€ºá€–á€­á€¯á€¸á€™á€™á€¾á€”á€ºá€•á€«á‹");
      return;
    }

    const changedItems = editItems.filter(
      (item) =>
        Number(item.unitPrice) !== Number(item.oldPrice)
    );

    if (!changedItems.length) {
      alert("á€•á€¼á€±á€¬á€„á€ºá€¸á€‘á€¬á€¸á€žá€±á€¬ Price á€™á€›á€¾á€­á€•á€«á‹");
      return;
    }

    const reason =
      window.prompt(
        `Price correction reason\nVoucher: ${editVoucher.voucherNo}`,
        "Wrong POS selling price"
      ) || "Wrong POS selling price";

    const confirmed = window.confirm(
      `Confirm voucher price correction?\n\nVoucher: ${editVoucher.voucherNo}\nChanged items: ${changedItems.length}\nEstimated Total: ${Number(
        editedVoucherTotal
      ).toLocaleString()} Ks\n\nStock quantity will NOT change.`
    );

    if (!confirmed) return;

    try {
      setEditSaving(true);

      const res = await fetch(
        `${API_BASE}/api/erp/sales-report/voucher/${encodeURIComponent(
          editVoucher.voucherNo
        )}/prices`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason,
            items: changedItems.map((item) => ({
              id: item.id,
              unit_price: Number(item.unitPrice),
            })),
          }),
        }
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json?.success === false) {
        throw new Error(
          json?.error ||
            json?.message ||
            "Voucher price correction failed"
        );
      }

      setEditVoucher(null);
      setEditItems([]);

      await loadReport();

      alert(
        `Voucher price corrected successfully.\n\nVoucher: ${json.voucherNo}\nNew Grand Total: ${Number(
          json.grandTotal || 0
        ).toLocaleString()} Ks\nStock Changed: NO`
      );
    } catch (err) {
      alert(err?.message || String(err));
    } finally {
      setEditSaving(false);
    }
  }

  // ALL_SHOP_MULTI_SHEET_EXCEL_V1
  function exportExcel() {
    if (!rows.length) {
      alert("No data to export");
      return;
    }

    const collator = new Intl.Collator("en", {
      numeric: true,
      sensitivity: "base",
    });

    const getVoucherSequence = (voucherNo) => {
      const match = String(voucherNo || "").trim().match(/-(\d+)$/);

      if (!match) return null;

      const value = Number(match[1]);
      return Number.isFinite(value) ? value : null;
    };

    const sortShopRows = (shopRows) =>
      [...shopRows].sort((left, right) => {
        const leftVoucher = String(left?.voucher_no || "").trim();
        const rightVoucher = String(right?.voucher_no || "").trim();

        const leftSequence = getVoucherSequence(leftVoucher);
        const rightSequence = getVoucherSequence(rightVoucher);

        if (leftSequence !== null && rightSequence !== null) {
          const sequenceCompare = leftSequence - rightSequence;

          if (sequenceCompare !== 0) return sequenceCompare;
        } else if (leftSequence !== null) {
          return -1;
        } else if (rightSequence !== null) {
          return 1;
        }

        return collator.compare(leftVoucher, rightVoucher);
      });

    const calculateSheetSummary = (shopRows) => {
      const voucherTotals = new Map();
      let qty = 0;
      let amount = 0;

      for (const row of shopRows) {
        const voucherNo = String(row?.voucher_no || "");

        qty += Number(row?.qty || 0);
        amount += Number(row?.amount || 0);

        if (voucherNo && !voucherTotals.has(voucherNo)) {
          voucherTotals.set(
            voucherNo,
            Number(row?.grand_total || 0),
          );
        }
      }

      return {
        vouchers: voucherTotals.size,
        qty,
        amount,
        grandTotal: Array.from(voucherTotals.values()).reduce(
          (sum, value) => sum + value,
          0,
        ),
      };
    };

    const createExportRows = (shopRows) =>
      sortShopRows(shopRows).map((row, index, sortedRows) => ({
        No: index + 1,
        Shop: row.shop_code || "",
        "Voucher No": row.voucher_no || "",
        "Sale Date": excelDateText(row.sale_date),
        "Customer Type": row.customer_type || "",
        Payment: row.payment_method || "",
        Channel: row.sale_source || row.saleSource || row.raw_sale?.sale_source || row.raw_sale?.saleSource || "",
        "Product Code": row.product_code || "",
        Note: getItemNote(row),
        Color: row.color || "",
        Size: row.size || "",
        Qty: Number(row.qty || 0),
        Price: Number(row.price || 0),
        Discount: Number(row.discount || 0),
        "Promotion Item": isFirstVoucherRow(row, index, sortedRows) ? getPromotionItems(row) : "",
        Amount: Number(row.amount || 0),
        "Voucher Grand Total": Number(row.grand_total || 0),
      }));

    const applySalesColumnWidths = (worksheet) => {
      worksheet["!cols"] = [
        { wch: 6 },
        { wch: 10 },
        { wch: 24 },
        { wch: 22 },
        { wch: 16 },
        { wch: 14 },
        { wch: 22 },
        { wch: 14 },
        { wch: 12 },
        { wch: 10 },
        { wch: 8 },
        { wch: 12 },
        { wch: 12 },
        { wch: 30 },
        { wch: 14 },
        { wch: 18 },
      ];
    };

    const groupedRows = new Map();

    for (const row of rows) {
      const shopCode = String(row?.shop_code || "UNKNOWN")
        .trim()
        .toUpperCase();

      if (!groupedRows.has(shopCode)) {
        groupedRows.set(shopCode, []);
      }

      groupedRows.get(shopCode).push(row);
    }

    const shopCodes = Array.from(groupedRows.keys()).sort((a, b) =>
      collator.compare(a, b),
    );

    const workbook = XLSX.utils.book_new();

    if (shop === "ALL") {
      const allSummaryRows = shopCodes.map((shopCode) => {
        const shopRows = groupedRows.get(shopCode) || [];
        const shopSummary = calculateSheetSummary(shopRows);

        return {
          Shop: shopCode,
          Vouchers: shopSummary.vouchers,
          "Total Qty": shopSummary.qty,
          "Line Amount": shopSummary.amount,
          "Voucher Grand Total": shopSummary.grandTotal,
        };
      });

      const overallSummary = [
        ["All Shop Sales Report"],
        ["Start Date", startDate],
        ["End Date", endDate],
        ["Search", search || "-"],
        ["Shop Count", shopCodes.length],
        ["Voucher Count", summary.vouchers],
        ["Total Qty", summary.qty],
        ["Line Amount Total", summary.amount],
        ["Voucher Grand Total", summary.grandTotal],
        [],
      ];

      const summarySheet = XLSX.utils.aoa_to_sheet(overallSummary);

      XLSX.utils.sheet_add_json(summarySheet, allSummaryRows, {
        origin: "A11",
        skipHeader: false,
      });

      summarySheet["!cols"] = [
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 18 },
        { wch: 22 },
      ];

      XLSX.utils.book_append_sheet(
        workbook,
        summarySheet,
        "ALL Summary",
      );
    }

    for (const shopCode of shopCodes) {
      const shopRows = groupedRows.get(shopCode) || [];
      const shopSummary = calculateSheetSummary(shopRows);
      const exportRows = createExportRows(shopRows);

      const headingRows = [
        ["All Shop Sales Report"],
        ["Shop", shopCode],
        ["Start Date", startDate],
        ["End Date", endDate],
        ["Search", search || "-"],
        ["Voucher Count", shopSummary.vouchers],
        ["Total Qty", shopSummary.qty],
        ["Line Amount Total", shopSummary.amount],
        ["Voucher Grand Total", shopSummary.grandTotal],
        [],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(headingRows);

      XLSX.utils.sheet_add_json(worksheet, exportRows, {
        origin: "A11",
        skipHeader: false,
      });

      applySalesColumnWidths(worksheet);

      const safeSheetName = shopCode
        .replace(/[\\/?*\[\]:]/g, "_")
        .slice(0, 31);

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        safeSheetName,
      );
    }

    const workbookOutput = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const fileName =
      shop === "ALL"
        ? `all-shops-separated-${startDate}-to-${endDate}.xlsx`
        : `${shop}-sales-${startDate}-to-${endDate}.xlsx`;

    saveAs(
      new Blob([workbookOutput], {
        type: "application/octet-stream",
      }),
      fileName,
    );
  }

  return (
    <Layout title="All Shop Sales Report">
      <div className="allSalesPage">
        <div className="allSalesHeader">
          <div>
            <h1>All Shop Sales Report</h1>
            <p>All shops sales report with line items and Excel export.</p>
          </div>

          <div className="allSalesActions">
            <Link className="allSalesCancelLink" to="/erp/cancelled-vouchers">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8h16v12H4zM3 4h18v4H3zM9 12h6"/></svg>
              Cancelled Vouchers
            </Link>
            <button type="button" onClick={loadReport} disabled={loading}>
              {loading ? "Loading..." : "Refresh"}
            </button>
            <button type="button" className="excelBtn" onClick={exportExcel} disabled={!rows.length}>
              Export Excel
            </button>
          </div>
        </div>

        <div className="allSalesFilters">
          <label>
            Shop
            <select value={shop} onChange={(e) => setShop(e.target.value)}>
              {SHOPS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>

          <label>
            Start Date
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>

          <label>
            End Date
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>

          <label className="searchBox">
            Search
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Voucher / product / shop / payment / channel"
            />
          </label>
        </div>

        {error ? <div className="allSalesError">{error}</div> : null}

        <div className="allSalesSummary">
          <div>
            <span>Rows</span>
            <b>{money(rows.length)}</b>
          </div>
          <div>
            <span>Vouchers</span>
            <b>{money(summary.vouchers)}</b>
          </div>
          <div>
            <span>Total Qty</span>
            <b>{money(summary.qty)}</b>
          </div>
          <div>
            <span>Line Amount</span>
            <b>{money(summary.amount)} Ks</b>
          </div>
          <div>
            <span>Voucher Grand Total</span>
            <b>{money(summary.grandTotal)} Ks</b>
          </div>
          <div>
            <span>SQL Voucher Total</span>
            <b>{money(total)}</b>
          </div>
        </div>

        <div className="allSalesTableWrap">
          <table className="allSalesTable">
            <thead>
              <tr>
                <th>No</th>
                <th>Shop</th>
                <th>Voucher No</th>
                <th>Sale Date</th>
                <th>Customer Type</th>
                <th>Payment</th>
                <th>Channel</th>
                <th>Product Code</th>
                <th>Note</th>
                <th>Color</th>
                <th>Size</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Discount</th>
                <th>Promotion Item</th>
                <th>Amount</th>
                <th>Voucher Total</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="18" className="emptyCell">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="18" className="emptyCell">
                    No sales found
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={`${row.voucher_no}-${index}`}>
                    <td>{index + 1}</td>
                    <td><b>{row.shop_code}</b></td>
                    <td className="voucherCell">{row.voucher_no}</td>
                    <td>{formatDateTime(row.sale_date)}</td>
                    <td>{row.customer_type || "-"}</td>
                    <td>{row.payment_method || "-"}</td>
                    <td>{row.sale_source || row.saleSource || row.raw_sale?.sale_source || row.raw_sale?.saleSource || "-"}</td>
                    <td className="codeCell">{row.product_code || "-"}</td>
                    <td>{getItemNote(row) || "-"}</td>
                    <td>{row.color || "-"}</td>
                    <td>{row.size || "-"}</td>
                    <td>{money(row.qty)}</td>
                    <td>{money(row.price)} Ks</td>
                    <td>{money(row.discount)} Ks</td>
                    <td className="promotionItemCell">
                      {isFirstVoucherRow(row, index, rows) ? getPromotionItems(row) || "-" : ""}
                    </td>
                    <td>{money(row.amount)} Ks</td>
                    <td>{money(row.grand_total)} Ks</td>
                    <td>
                      {index === 0 ||
                      rows[index - 1]?.voucher_no !== row.voucher_no ? (
                        <button
                          type="button"
                          className="voucherEditBtn"
                          onClick={() => openVoucherPriceEditor(row)}
                        >
                          Edit Voucher
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {editVoucher ? (
          <div className="voucherEditOverlay">
            <div className="voucherEditModal">
              <div className="voucherEditHeader">
                <div>
                  <h2>Edit Voucher Price</h2>
                  <p>
                    {editVoucher.voucherNo} Â· {editVoucher.shopCode}
                  </p>
                </div>

                <button
                  type="button"
                  className="voucherEditClose"
                  onClick={() => {
                    if (!editSaving) {
                      setEditVoucher(null);
                      setEditItems([]);
                    }
                  }}
                >
                  Ã—
                </button>
              </div>

              <div className="voucherEditWarning">
                Price correction only. Stock quantity will not change.
              </div>

              <div className="voucherEditTableWrap">
                <table className="voucherEditTable">
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Product</th>
                      <th>Color</th>
                      <th>Size</th>
                      <th>Qty</th>
                      <th>Old Price</th>
                      <th>New Price</th>
                      <th>New Amount</th>
                    </tr>
                  </thead>

                  <tbody>
                    {editItems.map((item, index) => (
                      <tr key={item.id}>
                        <td>{item.lineNo}</td>
                        <td>{item.productCode}</td>
                        <td>{item.color || "-"}</td>
                        <td>{item.size || "-"}</td>
                        <td>{money(item.qty)}</td>
                        <td>{money(item.oldPrice)} Ks</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={item.unitPrice}
                            onChange={(event) =>
                              updateEditItemPrice(
                                index,
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td>
                          {money(
                            Number(item.qty || 0) *
                              Number(item.unitPrice || 0)
                          )}{" "}
                          Ks
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="voucherEditFooter">
                <div>
                  Estimated New Total:
                  <b>{money(editedVoucherTotal)} Ks</b>
                </div>

                <div className="voucherEditActions">
                  <button
                    type="button"
                    onClick={() => {
                      setEditVoucher(null);
                      setEditItems([]);
                    }}
                    disabled={editSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="voucherEditSave"
                    onClick={saveVoucherPriceCorrection}
                    disabled={editSaving}
                  >
                    {editSaving
                      ? "Saving..."
                      : "Save Correction"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

      </div>
    </Layout>
  );
}
